import { Tooltip } from '@mui/material';
import { UnitSystem } from '@trackman/baseball-shared-components';
import { ServiceTypes } from 'constants/ServiceTypes';
import { LeagueStrikeZoneEnum, StrikeZoneDefinitionEnum } from 'interfaces/session/IStrikeZoneDefinition';
import { IApiLeagueStrikeZones } from 'interfaces/team/IApiScheduleResponse';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { LineupStore } from 'stores/LineupStore';
import StrikeZoneModal from '../StrikeZoneModal/StrikeZoneModal';
import { OfficialHomeplateSection } from './OfficialHomeplateSection/OfficialHomeplateSection';
import { StrikeZoneDescription, StrikeZonePriority, StrikeZoneTitle, StyledInfoIcon } from './OfficialStrikeZoneStyles';
import {
  Dimensions,
  Divider,
  SelectStrikeZoneWrapper,
  StrikeZone,
  StrikeZoneSelect,
  StyledRadio,
  StyledRadioGroup,
  Title,
} from './SelectStrikeZoneStyles';
import StrikeZoneImage from './StrikeZoneImage';

interface IProps {
  isPrivateGame: boolean;
}

const OfficialStrikeZone: React.FC<IProps> = observer((props) => {
  const { isPrivateGame } = props;
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const [selectedStrikeZone, setSelectedStrikeZone] = useState<keyof IApiLeagueStrikeZones>();
  const [modalVisible, setModalVisible] = useState(false);

  const onUnitSystemChange = (event: React.ChangeEvent<HTMLInputElement>, value: UnitSystem) => {
    lineupStore.setMetrics(value);
    lineupStore.recalculateStrikeZoneValues(value);
  };

  useEffect(() => {
    setSelectedStrikeZone(() => {
      switch (lineupStore.league.activeStrikeZone) {
        case StrikeZoneDefinitionEnum.Static:
          return LeagueStrikeZoneEnum.StaticStrikeZone;
        case StrikeZoneDefinitionEnum.HeightBased:
          return LeagueStrikeZoneEnum.HeightBasedStrikeZone;
        case StrikeZoneDefinitionEnum.TopAndBottomBased:
          return LeagueStrikeZoneEnum.TopAndBottomStrikeZone;
      }
    });
  }, []);

  useEffect(() => {
    lineupStore.setupOverridedStrikeZone();
  }, [lineupStore.league]);

  return (
    <SelectStrikeZoneWrapper>
      <StrikeZoneSelect>
        <Title>{lineupStore.league.name} league's strike zone</Title>
        <Divider />
        <Dimensions>
          <StyledRadioGroup name='strikezone' value={lineupStore.metrics} onChange={onUnitSystemChange}>
            <StyledRadio
              value={UnitSystem.Imperial}
              label={UnitSystem.Imperial}
              withoutBorder
              checked={lineupStore.metrics === UnitSystem.Imperial}
            />
            <StyledRadio
              value={UnitSystem.Metric}
              label={UnitSystem.Metric}
              withoutBorder
              checked={lineupStore.metrics === UnitSystem.Metric}
            />
          </StyledRadioGroup>
        </Dimensions>
      </StrikeZoneSelect>
      <OfficialHomeplateSection />
      <StrikeZone>
        <StrikeZoneImage selectedStrikeZone={selectedStrikeZone} isPrivateGame={isPrivateGame} />
      </StrikeZone>
      <Tooltip title='Read more' arrow componentsProps={{ tooltip: { className: 'game-dialog-tooltip' } }} placement='top'>
        <StrikeZonePriority onClick={() => setModalVisible(true)}>
          <StrikeZoneTitle>
            <StyledInfoIcon />
            <span>Strike zone priority</span>
          </StrikeZoneTitle>
          <StrikeZoneDescription>1)Top and bottom 2)Height based 3)Static</StrikeZoneDescription>
        </StrikeZonePriority>
      </Tooltip>
      <StrikeZoneModal closeHandler={() => setModalVisible(false)} visible={modalVisible} />
    </SelectStrikeZoneWrapper>
  );
});

export default OfficialStrikeZone;
